import { useEffect, useState } from "react";
import { Loader2, Mail, Save, Server } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  smtp_host: "",
  smtp_port: 587,
  smtp_user: "",
  smtp_password: "",
  smtp_from_email: "",
  smtp_from_name: "",
  smtp_use_tls: true,
  imap_host: "",
  imap_port: 993,
  imap_user: "",
  imap_password: "",
  imap_use_ssl: true,
  imap_folder: "INBOX",
  imap_poll_interval_minutes: 10,
  imap_enabled: false,
  default_commission_rate: 30,
  claim_recipient_email: "",
  claim_recipient_name: "",
  invoice_recipients: "",
};

export default function Settings() {
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({ smtp_password_set: false, imap_password_set: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [imapTesting, setImapTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const reload = async () => {
    try {
      const r = await api.get("/settings");
      const recipients = (r.data.invoice_recipients || []).join(", ");
      setForm({
        ...EMPTY,
        ...r.data,
        invoice_recipients: recipients,
        smtp_password: "",
        imap_password: "",
      });
      setMeta({
        smtp_password_set: !!r.data.smtp_password_set,
        imap_password_set: !!r.data.imap_password_set,
      });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const update = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        invoice_recipients: form.invoice_recipients
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        smtp_port: Number(form.smtp_port) || 587,
        imap_port: Number(form.imap_port) || 993,
        imap_poll_interval_minutes: Number(form.imap_poll_interval_minutes) || 10,
        default_commission_rate: Number(form.default_commission_rate) || 30,
      };
      // Only send password fields if non-empty
      if (!payload.smtp_password) delete payload.smtp_password;
      if (!payload.imap_password) delete payload.imap_password;

      await api.patch("/settings", payload);
      toast.success("Einstellungen gespeichert");
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const testSmtp = async () => {
    if (!testEmail.trim()) {
      toast.error("Bitte eine Test-Empfängeradresse eingeben");
      return;
    }
    setSmtpTesting(true);
    try {
      await api.post("/settings/smtp-test", { to_email: testEmail.trim() });
      toast.success("Testmail gesendet");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSmtpTesting(false);
    }
  };

  const testImap = async () => {
    setImapTesting(true);
    try {
      const r = await api.post("/settings/imap-test");
      toast.success(`IMAP-Verbindung OK – ${r.data.found_attachments} Anhang/Anhänge gefunden`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setImapTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Einstellungen …
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Einstellungen"
        subtitle="Konfiguration"
        actions={
          <Button
            onClick={save}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="settings-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
            Speichern
          </Button>
        }
      >
        E-Mail-Server, Postbox-Polling und Standardwerte für Provisionen.
      </PageHeader>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-8 space-y-6">
        {/* General */}
        <Section icon={Save} title="Allgemein">
          <Field label="Standard-Provisionssatz (%)">
            <Input
              type="number"
              step="0.1"
              value={form.default_commission_rate}
              onChange={update("default_commission_rate")}
              data-testid="settings-default-rate"
            />
          </Field>
          <Field label="Empfänger-E-Mails für „Invoice anfordern“ (Komma-getrennt)">
            <Input
              value={form.invoice_recipients}
              onChange={update("invoice_recipients")}
              placeholder="julia@gastro-beratung.de, maximilian@gastro-beratung.de"
              data-testid="settings-invoice-recipients"
            />
          </Field>
          <Field label="Standard-Empfänger für Claims (Lightspeed Consultant)">
            <Input
              value={form.claim_recipient_email}
              onChange={update("claim_recipient_email")}
              placeholder="karim.kessab@lightspeedhq.com"
              data-testid="settings-claim-recipient"
            />
          </Field>
          <Field label="Name des Claim-Empfängers">
            <Input
              value={form.claim_recipient_name}
              onChange={update("claim_recipient_name")}
              placeholder="Karim Kessab"
              data-testid="settings-claim-recipient-name"
            />
          </Field>
        </Section>

        {/* SMTP */}
        <Section icon={Mail} title="SMTP (ausgehende E-Mails)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="SMTP Host">
              <Input value={form.smtp_host} onChange={update("smtp_host")} data-testid="settings-smtp-host" />
            </Field>
            <Field label="Port">
              <Input type="number" value={form.smtp_port} onChange={update("smtp_port")} data-testid="settings-smtp-port" />
            </Field>
            <Field label="Benutzer">
              <Input value={form.smtp_user} onChange={update("smtp_user")} data-testid="settings-smtp-user" />
            </Field>
            <Field label={meta.smtp_password_set ? "Passwort (••••••)" : "Passwort"}>
              <Input
                type="password"
                value={form.smtp_password}
                onChange={update("smtp_password")}
                placeholder={meta.smtp_password_set ? "Leer lassen, um aktuelles zu behalten" : ""}
                data-testid="settings-smtp-password"
              />
            </Field>
            <Field label="From-Adresse">
              <Input value={form.smtp_from_email} onChange={update("smtp_from_email")} data-testid="settings-smtp-from-email" />
            </Field>
            <Field label="From-Name">
              <Input value={form.smtp_from_name} onChange={update("smtp_from_name")} data-testid="settings-smtp-from-name" />
            </Field>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Switch
              id="smtp_tls"
              checked={!!form.smtp_use_tls}
              onCheckedChange={(v) => setForm((p) => ({ ...p, smtp_use_tls: v }))}
              data-testid="settings-smtp-tls"
            />
            <Label htmlFor="smtp_tls">STARTTLS verwenden (Port 587)</Label>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mt-2 flex gap-2 items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="smtp_test_to">Test-E-Mail an</Label>
              <Input
                id="smtp_test_to"
                placeholder="test@beispiel.de"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                data-testid="settings-smtp-test-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={testSmtp}
              disabled={smtpTesting}
              className="rounded-sm"
              data-testid="settings-smtp-test-btn"
            >
              {smtpTesting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Senden
            </Button>
          </div>
        </Section>

        {/* IMAP */}
        <Section icon={Server} title="IMAP (eingehende Postbox)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="IMAP Host">
              <Input value={form.imap_host} onChange={update("imap_host")} data-testid="settings-imap-host" />
            </Field>
            <Field label="Port">
              <Input type="number" value={form.imap_port} onChange={update("imap_port")} data-testid="settings-imap-port" />
            </Field>
            <Field label="Benutzer">
              <Input value={form.imap_user} onChange={update("imap_user")} data-testid="settings-imap-user" />
            </Field>
            <Field label={meta.imap_password_set ? "Passwort (••••••)" : "Passwort"}>
              <Input
                type="password"
                value={form.imap_password}
                onChange={update("imap_password")}
                placeholder={meta.imap_password_set ? "Leer lassen, um aktuelles zu behalten" : ""}
                data-testid="settings-imap-password"
              />
            </Field>
            <Field label="Ordner">
              <Input value={form.imap_folder} onChange={update("imap_folder")} data-testid="settings-imap-folder" />
            </Field>
            <Field label="Poll-Intervall (Minuten)">
              <Input
                type="number"
                value={form.imap_poll_interval_minutes}
                onChange={update("imap_poll_interval_minutes")}
                data-testid="settings-imap-interval"
              />
            </Field>
          </div>
          <div className="flex items-center gap-6 pt-2">
            <div className="flex items-center gap-3">
              <Switch
                id="imap_ssl"
                checked={!!form.imap_use_ssl}
                onCheckedChange={(v) => setForm((p) => ({ ...p, imap_use_ssl: v }))}
                data-testid="settings-imap-ssl"
              />
              <Label htmlFor="imap_ssl">SSL/TLS</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="imap_enabled"
                checked={!!form.imap_enabled}
                onCheckedChange={(v) => setForm((p) => ({ ...p, imap_enabled: v }))}
                data-testid="settings-imap-enabled"
              />
              <Label htmlFor="imap_enabled">Polling aktivieren</Label>
            </div>
            <Button
              variant="outline"
              onClick={testImap}
              disabled={imapTesting}
              className="rounded-sm ml-auto"
              data-testid="settings-imap-test-btn"
            >
              {imapTesting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Verbindung testen
            </Button>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
        <Icon size={16} className="text-slate-500" />
        <h3 className="font-display text-lg font-bold">{title}</h3>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
